import {Dialog, Transition} from '@headlessui/react';
import {Fragment, useEffect, useState} from 'react';
import {useTranslations} from '../../contexts/Translations';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgotPasswordForm, {type AuthView} from './ForgotPasswordForm';
import {useUser} from '../../contexts/User';
import {Spinner} from '../Spinner';

export interface AuthFormProps {
    /** Optional CSS classes for the modal dialog */
    modalClassNames?: string;
    /** Optional CSS classes for the login button */
    buttonClassNames?: string;
    /** URL to redirect to after successful login/registration */
    urlAfter?: string;
    /** Label text for the login button */
    label: string;
}

/**
 * AuthForm Component
 *
 * A self-contained authentication component that renders a login button and modal dialog.
 * Supports email/password authentication and OAuth providers (Google, Discord).
 *
 * @example
 * ```tsx
 * <AuthForm 
 *   buttonClassNames="btn-primary rounded-full"
 *   modalClassNames="max-w-lg"
 *   urlAfter="/dashboard"
 *   label={translated('login')}
 * />
 * ```
 */
export function AuthForm({
    modalClassNames = '',
    buttonClassNames = '',
    urlAfter,
    label
}: AuthFormProps) {
    const {loading, authDialogIsOpen, setAuthDialogIsOpen, setAuthRedirectAfter} = useUser();
    useEffect(() => {
        if (urlAfter) {
            setAuthRedirectAfter(urlAfter);
        }
    }, [urlAfter, setAuthRedirectAfter]);

    const defaultButtonClasses =
        'inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

    return (
        <div>
            <AuthButton label={label} buttonClassNames={buttonClassNames}/>

            {loading && <Spinner className="w-7 h-7"/>}

            {authDialogIsOpen && (
                <AuthModal modalClassNames={modalClassNames}/>
            )}
        </div>
    );
}

export function AuthButton({
   buttonClassNames = '',
    label = 'Login'
}: AuthFormProps) {
    const {loading, setAuthDialogIsOpen, setAuthRedirectAfter} = useUser();
    const defaultButtonClasses = 'inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
    return (
        <button
            disabled={loading}
            className={buttonClassNames || defaultButtonClasses}
            onClick={() => setAuthDialogIsOpen(true)}
            aria-label={label}
        >
            {label}
        </button>
    )
}



interface AuthModalProps {
    modalClassNames?: string;
}

export function AuthModal({modalClassNames = ''}: AuthModalProps) {
    const {user, authDialogIsOpen, setAuthDialogIsOpen, authRedirectAfter} = useUser();
    const [view, setView] = useState<AuthView>('login');
    const {translated} = useTranslations();

    useEffect(() => {
        if (user) {
            // User successfully authenticated
            if (authRedirectAfter) {
                // Redirect to specified URL
                window.location.href = authRedirectAfter;
            } else {
                // Just close modal and stay on current page
                setAuthDialogIsOpen(false);
            }
        }
    }, [user, authRedirectAfter, setAuthDialogIsOpen]);

    function closeModal() {
        setAuthDialogIsOpen(false);
    }

    const getTitle = () => {
        switch (view) {
            case 'register':
                return translated('register');
            case 'forgotPassword':
                return translated('forgot_password_title') || 'Reset Password';
            default:
                return translated('login');
        }
    };

    if(!authDialogIsOpen) return null;
    const defaultModalClasses = 'w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all';
    return (
        <Transition appear show={authDialogIsOpen} as={Fragment}>
            {/* `be-embedded-app` sits on the Dialog root so the scoped Tailwind
                CSS (every selector is prefixed `.be-embedded-app …`) reaches
                this subtree even though HeadlessUI portals it to document.body.
                The inner wrapper carries the positioning utilities — keeping
                `.relative .z-[10000]` on a *descendant* of `.be-embedded-app`
                so the descendant-combinator scoping rules actually match.
                (`.be-embedded-app .relative` does NOT match a single element
                that has both classes on itself.) */}
            <Dialog as="div" className="be-embedded-app" onClose={closeModal}>
                <div className="relative z-[10000]">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/25" aria-hidden="true"/>
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className={modalClassNames || defaultModalClasses}>
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-medium leading-6 text-gray-900"
                                    >
                                        {getTitle()}
                                    </Dialog.Title>

                                    {view === 'login' && <LoginForm setView={setView}/>}
                                    {view === 'register' && <RegisterForm setView={setView}/>}
                                    {view === 'forgotPassword' && <ForgotPasswordForm setView={setView}/>}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
